import React, { useState, useEffect, useCallback } from 'react'
import { Modal } from 'react-bootstrap'
import styled, { useTheme } from 'styled-components'

import PerspectiveSelect from 'live/components/forms/ui/perspective-select'
import List, { Order, OrderingHeader, toggleOrder } from 'live/components/ui/List'
import { DashboardContext } from 'live/context/dashboard'
import DateService from 'live/services/date'
import i18n from 'live/services/i18n'

import { fetchDashboards, getDashboardLinkNavigation, updateDashboardLinkNavigation } from '../services/api'
import { mountDashboardLink } from '../utils/mountDashboardLink'
import { useSpan } from '../hooks/useSpan'

export const LinkDashboardModal = (): JSX.Element => {
    const [show, setShow] = React.useState(false)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const [dashboards, setDashboards] = useState<LiveDashboard[]>([])
    const [saving, setSaving] = useState(false)
    const [saved, setSaved] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [perspective, setPerspective] = useState(null)
    const dashboardContext = React.useContext(DashboardContext)
    const [dashboard, setDashboard] = React.useState<LiveDashboard | Record<string, never>>({})
    const [selectedDashboard, setSelectedDashboard] = useState<LiveDashboard[]>([])
    const [dashboardsLinked, setDashboardsLinked] = React.useState<LiveDashboard[]>([])

    const [order, updateOrder] = useState<Order>({
        prop: 'title',
        dir: 'asc'
    })

    const theme = useTheme()

    const { span } = useSpan(dashboardContext)

    React.useEffect(() => {
        if (dashboardContext?.dashboard?.id) {
            setDashboard(dashboardContext.dashboard)
            getDashboardLinkNavigation(dashboardContext.dashboard.id).then((resp: LiveDashboard[]) => {
                if (resp.length > 0) {
                    setDashboardsLinked(resp)
                    setSelectedDashboard(resp)
                }
            })
        }
    }, [dashboardContext])

    const loadDashboards = useCallback(() => {
        fetchDashboards()
            .then(({ data }) => {
                // Ignore Dashboards with visibility "draft" and ignore current Dashboard
                const visibleDashboards = data.data.filter(e => e.id !== dashboardContext?.dashboard?.id)
                setDashboards(visibleDashboards)
                setLoading(false)
            })
            .catch(err => {
                console.error('Error fetching dashboards', err)
                setLoading(false)
                setError(true)
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const retry = useCallback(() => {
        setLoading(true)
        setError(false)
        loadDashboards()
    }, [loadDashboards])

    useEffect(() => {
        if (!show) {
            setSelectedDashboard([])
            setSaving(false)
            setSearchTerm('')
            setPerspective(null)
        }
    }, [show])

    useEffect(() => {
        if (show && loading) {
            loadDashboards()
        }
    }, [show, loading, loadDashboards])

    const onSave = (dashboards: LiveDashboard[]): void => {
        if (dashboard?.id) {
            const hasDashboardsLinked = selectedDashboard.length > 0 ? selectedDashboard : []

            dashboards?.forEach(d => {
                if (hasDashboardsLinked.map(e => e.id).indexOf(d.id) < 0) {
                    hasDashboardsLinked.push(d)
                }
            })

            updateDashboardLinkNavigation(dashboard.id, hasDashboardsLinked)

            setTimeout(() => {
                setSaving(false)
                setSaved(true)
            }, 500)
        }
    }

    const save = (): void => {
        setSaving(true)
        onSave(selectedDashboard)
    }

    const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchTerm(e.target.value)
    }

    // FILTER DISABLE FOR NOW
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const filterFn = (item: LiveDashboard): boolean => {
        // const perspectiveFilter = perspective ? item.perspective.id === perspective.id : true
        // const found = dashboardsLinked.find(att => att.id === item.id)

        // If you want to be return only dashboards not selected change the return "found" to "!found"
        // return perspectiveFilter && found
        return true
    }

    const setOrder = (prop: string, defaultOrder: Order['dir']): void => {
        updateOrder((oldOrder: Order) => toggleOrder(oldOrder, prop, defaultOrder))
    }

    const onChangePerspective = (p: { perspective: { id: number } }): void => {
        setPerspective(p.perspective)
    }

    const clearSearch = (): void => {
        setPerspective(null)
        setSearchTerm('')
    }

    const onHide = (): void => {
        setShow(!show)
        // document.location.reload()
    }

    const Button = (): JSX.Element => {
        return (
            <li className={'dashboard-options dropdown-container editors-list'}>
                <div className={'item-container'} onClick={() => setShow(!show)}>
                    <i className="fa fa-chart-bar"></i>
                    <span>LINK DASHBOARDS</span>
                </div>
            </li>
        )
    }

    function checkIfHasDashboardInList(listDashboard: LiveDashboard[], dashboard: LiveDashboard): boolean {
        return listDashboard.map(e => e.id).indexOf(dashboard.id) !== -1
    }

    const onClick = (dashboard): void => {
        if (
            checkIfHasDashboardInList(selectedDashboard, dashboard) ||
            checkIfHasDashboardInList(dashboardsLinked, dashboard)
        ) {
            setSelectedDashboard(selectedDashboard.filter(s => s.id !== dashboard.id))
            setDashboardsLinked(dashboardsLinked.filter(s => s.id !== dashboard.id))
        } else {
            setSelectedDashboard([...selectedDashboard, dashboard])
            setDashboardsLinked([...dashboardsLinked, dashboard])
        }
        setSaved(false)
    }

    return (
        <>
            {<Button />}
            <Modal className="modal-lg" onHide={onHide} show={show}>
                <Modal.Header>
                    <Modal.Title>{i18n('Choose a dashboard to link')}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ paddingBottom: 0, minHeight: '400px' }}>
                    <FiltersContainer>
                        <div>
                            <input
                                type="text"
                                className="search no-box-shadow"
                                placeholder={i18n('Search by dashboard name')}
                                value={searchTerm}
                                onChange={onChangeSearch}
                            />
                        </div>
                        <div>
                            <PerspectiveSelect
                                placeholder={i18n('Filter by perspective')}
                                apply={onChangePerspective}
                            />
                        </div>
                    </FiltersContainer>
                    {searchTerm || perspective ? (
                        <div className="text-right">
                            <a onClick={clearSearch}>{i18n('Clear search')}</a>
                        </div>
                    ) : null}
                    <List
                        searchTerm={searchTerm}
                        searchFields={['title']}
                        filterFn={filterFn}
                        filteredBy={{ perspective }}
                        items={dashboards}
                        order={order}
                        sort={true}
                        render={items => {
                            return (
                                <WideTable className="table table-hover">
                                    <thead>
                                        <tr>
                                            <OrderingHeader
                                                onClick={() => setOrder('title', 'asc')}
                                                tag="th"
                                                direction={order.dir}
                                                showArrow={order.prop === 'title'}
                                            >
                                                <>{i18n('DASHBOARD NAME')}</>
                                            </OrderingHeader>

                                            <OrderingHeader
                                                onClick={() => setOrder('dateModified', 'desc')}
                                                tag="th"
                                                direction={order.dir === 'asc' ? 'desc' : 'asc'}
                                                showArrow={order.prop === 'dateModified'}
                                            >
                                                <>{i18n('LAST CHANGE')}</>
                                            </OrderingHeader>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.length ? (
                                            items.map((dashboard: LiveDashboard, idx) => {
                                                return (
                                                    <DashboardRow key={idx} onClick={() => onClick(dashboard)}>
                                                        <td>
                                                            <input
                                                                type="radio"
                                                                checked={
                                                                    selectedDashboard?.length > 0
                                                                        ? checkIfHasDashboardInList(
                                                                              selectedDashboard,
                                                                              dashboard
                                                                          )
                                                                        : dashboardsLinked?.length > 0
                                                                        ? checkIfHasDashboardInList(
                                                                              selectedDashboard,
                                                                              dashboard
                                                                          )
                                                                        : false
                                                                }
                                                            />

                                                            {dashboard.title}
                                                        </td>
                                                        <td>{DateService.fromNowCalendar(dashboard.dateModified)}</td>
                                                        <td>
                                                            <a
                                                                onClick={e => e.stopPropagation()}
                                                                rel="noopener noreferrer"
                                                                href={mountDashboardLink(
                                                                    dashboard.id,
                                                                    span,
                                                                    dashboardContext?.dashboard
                                                                )}
                                                            >
                                                                <i className="fa fa-external-link" />
                                                            </a>
                                                        </td>
                                                    </DashboardRow>
                                                )
                                            })
                                        ) : (
                                            <DashboardRow>
                                                <td
                                                    colSpan={3}
                                                    style={{ fontFamily: theme['font-regular'] }}
                                                    className="text-center"
                                                >
                                                    {error ? (
                                                        <div>
                                                            {i18n('Error loading dashboards')}{' '}
                                                            <a onClick={retry}>{`${i18n('Retry')} ?`}</a>
                                                        </div>
                                                    ) : loading ? (
                                                        `${i18n('Loading')}...`
                                                    ) : dashboardsLinked.length ? (
                                                        `${i18n('All visible dashboards are already linked')}.`
                                                    ) : (
                                                        `${i18n('No dashboards found')}.`
                                                    )}
                                                </td>
                                            </DashboardRow>
                                        )}
                                    </tbody>
                                </WideTable>
                            )
                        }}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <div>
                        <button className={`btn btn-${saved ? 'success' : 'primary'}`} onClick={save} disabled={saving}>
                            {saving ? (
                                <span>
                                    <i className="fa fa-spinner fa-spin" /> {i18n('Saving')}
                                </span>
                            ) : saved ? (
                                <span>{i18n('Saved')}</span>
                            ) : (
                                <span>{i18n('Save')}</span>
                            )}
                        </button>
                        <span>{i18n('or')}</span>
                        <a className="cancel" onClick={onHide}>
                            {i18n('cancel')}
                        </a>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    )
}

const FiltersContainer = styled.div`
    display: flex;

    .form-group {
        margin-bottom: 0;
    }

    > div:nth-of-type(1) {
        flex: 1;
        margin-right: 15px;

        > input {
            height: 36px;
            border-radius: 3px;
        }
    }

    > div:nth-of-type(2) {
        width: 200px;
    }
`
const WideTable = styled.table`
    margin-left: -25px;
    margin-right: -25px;
    width: calc(100% + 50px);
    max-width: none;
    color: ${({ theme }) => theme['text-color']};

    thead > tr > th {
        color: ${({ theme }) => theme['text-color']};

        &:first-of-type {
            padding-left: 42px;
        }

        i {
            margin-left: 5px;
            transition: transform 0.25s ease-out;
        }
    }

    tbody {
        display: block;
        max-height: 496px;
        overflow: auto;

        &::-webkit-scrollbar {
            width: 6px;
        }

        &::-webkit-scrollbar-track {
            background: transparent;
            border-radius: 0;
            margin: 1px;
        }

        &::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0);
            border-radius: 0;
            background-clip: padding-box;
            box-shadow: ${`inset 4px 0px 0px 0px ${({ theme }) => theme.gray}, -1px 0px 0px ${({ theme }) =>
                theme.gray}`};
            -webkit-box-shadow: ${`inset 4px 0px 0px 0px ${({ theme }) => theme.gray}, -1px 0px 0px ${({ theme }) =>
                theme.gray}`};
        }
    }
    thead,
    tbody tr {
        display: table;
        width: 100%;
        table-layout: fixed;
    }
    thead th:nth-of-type(2) {
        width: 175px;
    }
    tbody td:nth-of-type(1) {
        font-size: 14px;
        font-family: ${({ theme }) => theme['font-bold']};
    }
    tbody td:nth-of-type(2) {
        width: 170px;
    }
    thead th:nth-of-type(3),
    tbody td:nth-of-type(3) {
        width: 50px;
    }
    tr {
        cursor: pointer;

        td {
            padding-left: 26px;
            padding-right: 36px;
        }

        &:hover {
        }
    }
`

const DashboardRow = styled.tr`
    background-color: ${({ theme }) => theme.pearl};

    &:hover {
        td a {
            color: ${({ theme }) => theme['link-color']} !important;
        }
    }

    td {
        height: 70px;
        vertical-align: middle;

        a {
            color: ${({ theme }) => theme['link-color']};
        }

        &:nth-of-type(2) {
            font-size: 12px;
            color: ${({ theme }) => theme.gray};
        }

        input[type='radio'] {
            position: relative;
            top: 2px;
            margin-right: 14px;
        }
    }
`
